import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { QueryErrorState } from '@/components/admin/shared/QueryErrorState';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { Button } from '@/components/ui/button';
import { ClientFormModal } from '@/components/practitioner/ClientsPage/ClientFormModal';
import { PortalStatusPill } from '@/components/practitioner/ClientsPage/PortalStatusPill';
import {
  useArchiveClientMutation,
  useRestoreClientMutation,
} from '@/hooks/useClientMutations';
import { useAuth } from '@/hooks/useAuth';
import { useClientQuery } from '@/hooks/useClientQuery';
import { useHasWorkspaceModule } from '@/hooks/useHasWorkspaceModule';
import { MODULE_KEY } from '@/constants/modules';
import { buildNameInitials } from '@/lib/nameInitials';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';
import { AccessHistoryPanel } from './AccessHistoryPanel';
import { PortalActivityPanel } from './PortalActivityPanel';
import { ContactCard } from './ContactCard';
import { ConsentsPanel } from './ConsentsPanel';
import { PetsPanel } from './PetsPanel';
import { ClientPrivacyPanel } from './ClientPrivacyPanel';
import { InvitePanel } from './InvitePanel';
import { AssistantTargetRegion } from '@/components/assistant/uiActions/AssistantTargetRegion';
import { ASSISTANT_TARGET_KEY } from '@/components/assistant/uiActions/assistantTargetKeys';

export function ClientDetailPage() {
  const { t } = useTranslation(['clients', 'common']);
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId: string }>();
  const resolvedId = clientId === undefined ? '' : clientId;
  const clientQuery = useClientQuery(resolvedId);
  const hasConsents = useHasWorkspaceModule(MODULE_KEY.consents);
  const { activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const isOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const archiveMutation = useArchiveClientMutation();
  const restoreMutation = useRestoreClientMutation();
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isConfirmingArchive, setIsConfirmingArchive] = useState(false);

  const client = clientQuery.data;
  const isArchived = client !== undefined && client.status === 'archived';

  function confirmArchive() {
    if (client === undefined) {
      return;
    }
    archiveMutation.mutate(client.id, {
      onSuccess: function done() {
        showSuccessNotification(t('clients:detail.archived'));
        setIsConfirmingArchive(false);
      },
      onError: function fail(archiveError) {
        showErrorNotification(resolveApiErrorMessage(archiveError));
        setIsConfirmingArchive(false);
      },
    });
  }

  function restore() {
    if (client === undefined) {
      return;
    }
    restoreMutation.mutate(client.id, {
      onSuccess: function done() {
        showSuccessNotification(t('clients:detail.restored'));
      },
      onError: function fail(restoreError) {
        showErrorNotification(resolveApiErrorMessage(restoreError));
      },
    });
  }

  return (
    <div className="p-6 md:p-8">
      <button
        type="button"
        onClick={function goBack() {
          navigate(APPLICATION_ROUTES.workspaceClients);
        }}
        className="flex items-center gap-1.5 min-h-6 text-sm text-zoomuted hover:text-zooprimary transition-colors mb-4"
      >
        <Icon name="ArrowLeft" />
        {t('clients:detail.back')}
      </button>

      {clientQuery.isLoading === true ? (
        <div className="py-16 flex justify-center">
          <LoadingSpinner className="w-7 h-7" />
        </div>
      ) : clientQuery.isError === true || client === undefined ? (
        <QueryErrorState
          error={clientQuery.error}
          onRetry={function retry() {
            clientQuery.refetch();
          }}
        />
      ) : (
        <>
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <div className="flex items-center gap-3 flex-1 basis-64 min-w-0">
              <span className="w-12 h-12 rounded-full bg-zoovpalelavender text-zooprimary flex items-center justify-center font-bold shrink-0">
                {buildNameInitials(client.fullName)}
              </span>
              <div className="min-w-0">
                <h1 className="text-2xl font-bold text-zooindigo break-words">{client.fullName}</h1>
                <div className="text-[12px] text-zoomuted">{t('clients:detail.addedOn')}</div>
              </div>
            </div>
            <PortalStatusPill status={client.portalStatus} />
            {client.anonymisedAt !== null ? null : isArchived === true ? (
              <Button variant="outline" size="chip" onClick={restore}>
                <Icon name="RefreshCw" /> {t('clients:detail.restore')}
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="chip"
                  onClick={function openEdit() {
                    setIsEditOpen(true);
                  }}
                >
                  <Icon name="Edit" /> {t('clients:detail.edit')}
                </Button>
                <Button
                  variant="destructiveGhost"
                  size="chip"
                  className="border border-zooborder"
                  onClick={function openArchive() {
                    setIsConfirmingArchive(true);
                  }}
                >
                  <Icon name="Trash" /> {t('clients:detail.archive')}
                </Button>
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-5">
            <div className="space-y-5">
              <ContactCard client={client} />
              {client.anonymisedAt === null ? (
                <AssistantTargetRegion
                  targetKey={ASSISTANT_TARGET_KEY.clientInvitationStatus}
                  entityId={client.id}
                >
                  <InvitePanel client={client} />
                </AssistantTargetRegion>
              ) : null}
              <ClientPrivacyPanel client={client} />
            </div>
            <div className="space-y-5">
              <PortalActivityPanel clientId={client.id} />
              {hasConsents === false ? null : <ConsentsPanel clientId={client.id} />}
              <PetsPanel clientId={client.id} />
              {isOwner === true ? <AccessHistoryPanel clientId={client.id} /> : null}
            </div>
          </div>
        </>
      )}

      {isEditOpen === true && client !== undefined ? (
        <ClientFormModal
          client={client}
          onClose={function closeEdit() {
            setIsEditOpen(false);
          }}
        />
      ) : null}

      {isConfirmingArchive === true && client !== undefined ? (
        <ConfirmDialog
          tone="danger"
          iconName="Trash"
          title={t('clients:detail.archiveTitle')}
          description={t('clients:detail.archiveDescription', { name: client.fullName })}
          confirmLabel={t('clients:detail.archive')}
          isConfirming={archiveMutation.isPending}
          onConfirm={confirmArchive}
          onCancel={function cancel() {
            setIsConfirmingArchive(false);
          }}
        />
      ) : null}
    </div>
  );
}
